import {
  custom,
  http,
  type Address,
  type Chain as ViemChain,
  type EIP1193Provider,
  type HttpTransport,
  type JsonRpcAccount,
} from 'viem'
import type { Eip1193Provider } from 'ethers'
import { createShieldedPublicClient, createShieldedWalletClient, seismicTestnet } from 'seismic-viem'
import type { ShieldedWalletClient } from 'seismic-viem'

type ShieldedClientParams = {
  /** The connected wallet's EIP-1193 provider (web3-onboard `wallet.provider`). Used for signing. */
  provider: Eip1193Provider
  /** The connected signer address. Becomes a json-rpc account so signing routes to eth_signTypedData_v4. */
  address: Address
  /** RPC URL of the shielded chain, used for the public client (reads, TEE key, raw submission). */
  rpcUrl: string
}

/**
 * Builds the chain config for our self-hosted Seismic endpoint. We reuse `seismicTestnet`'s
 * formatters/serializers (the shielded transaction encoding) and only override the RPC URL so the
 * client talks to our gateway rather than Seismic's default GCP node.
 */
const getShieldedChain = (rpcUrl: string): ViemChain => ({
  ...seismicTestnet,
  rpcUrls: { default: { http: [rpcUrl] } },
})

/**
 * Creates a seismic-viem shielded wallet client backed by an injected wallet (MetaMask).
 *
 * The account is a json-rpc account, so seismic-viem signs the messageVersion-2 EIP-712 envelope via
 * `eth_signTypedData_v4` (which MetaMask supports) instead of an RLP-encoded Seismic transaction.
 * Public requests (TEE key, nonce, fees, raw submission) go through the http public client to the
 * shielded RPC, while signing goes through the injected provider transport.
 */
export const getShieldedWalletClient = async ({
  provider,
  address,
  rpcUrl,
}: ShieldedClientParams): Promise<ShieldedWalletClient> => {
  const chain = getShieldedChain(rpcUrl)

  const publicClient = createShieldedPublicClient({
    chain,
    transport: http(rpcUrl),
  })

  const account: JsonRpcAccount = { address, type: 'json-rpc' }

  // seismic-viem couples the wallet transport and the public-client transport in one generic, but we
  // intentionally use a `custom()` wallet transport (MetaMask, for signing) alongside an `http()`
  // public client (the shielded RPC). seismic-react resolves this with `@ts-ignore`; we use scoped
  // casts. Runtime behaviour is unaffected — only the static types are bridged.
  const client = await createShieldedWalletClient({
    chain,
    account,
    transport: custom(provider as unknown as EIP1193Provider) as unknown as HttpTransport,
    publicClient,
  })

  return client as unknown as ShieldedWalletClient
}
